import React, { useState, useEffect, useMemo } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './HolidayCalendarModal.css'; //
import Modal from '../ChatSetup/components/Modal';
import { Message } from "semantic-ui-react";
import api from "../../../api";

const HolidayCalendarModal = ({ show, onClose, onDone, existingHolidays = [] }) => {
    const [selectedDates, setSelectedDates] = useState([]);
    const [publicHolidays, setPublicHolidays] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show) {
            fetchPublicHolidays();
        }
    }, [show]);

    const fetchPublicHolidays = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await api.botbuilder.getHolidaysListForOutbound();
            const holidays = result.holidays.data || [];

            // Group holidays by date to handle multiple holidays on the same date
            const holidayMap = new Map();
            holidays.forEach((holiday) => {
                const date = holiday.date ?? ""
                const name = holiday.localName ?? "";
                const localName = holiday.localName ?? name;
                const countryCode = holiday.countryCode ?? "";

                if (holidayMap.has(date)) {
                    // If date already exists, add to the array
                    holidayMap.get(date).push({ date, name, localName, countryCode });
                } else {
                    // Create new array for this date
                    holidayMap.set(date, [{ date, name, localName, countryCode }]);
                }
            });

            // Convert Map to array of objects with date and holidays array
            const groupedHolidays = Array.from(holidayMap.entries()).map(([date, holidays]) => ({
                date,
                holidays
            }));

            setPublicHolidays(groupedHolidays);

        } catch (err) {
            console.error('Error fetching holidays:', err);
            setError('Failed to load public holidays. You can still select dates manually.');
        } finally {
            setLoading(false);
        }
    };

    //  O(1) lookup - now stores array of holidays per date
    const holidayLookup = useMemo(() => {
        const map = new Map();
        publicHolidays.forEach(item => map.set(item.date, item.holidays));
        return map;
    }, [publicHolidays]);

    //  existing holidays optimization
    const existingHolidaySet = useMemo(() => {
        return new Set(existingHolidays.map(h => h.date));
    }, [existingHolidays]);

    const formatDateForAPI = (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const isPublicHoliday = (date) => {
        const dateStr = formatDateForAPI(date);
        return holidayLookup.get(dateStr);
    };

    const handleDateClick = (date) => {
        const dateStr = formatDateForAPI(date);

        //  prevent selecting existing campaign holidays
        if (existingHolidaySet.has(dateStr)) return;

        setSelectedDates(prev => {
            const exists = prev.find(d => d.date === dateStr);

            if (exists) {
                return prev.filter(d => d.date !== dateStr);
            }

            const holidays = holidayLookup.get(dateStr);

            // If multiple holidays exist on this date, join their names
            let holidayName = '';
            if (holidays && holidays.length > 0) {
                holidayName = holidays.map(h => h.name).join(', ');
            }

            return [
                ...prev,
                {
                    date: dateStr,
                    holiday_name: holidayName,
                    isPublicHoliday: !!holidays
                }
            ];
        });
    };

    const tileClassName = ({ date }) => {
        const dateStr = formatDateForAPI(date);
        const isSelected = selectedDates.some(d => d.date === dateStr);
        const isExisting = existingHolidaySet.has(dateStr);
        const isHoliday = holidayLookup.has(dateStr);

        let classes = [];
        if (isSelected) classes.push('selected-holiday');
        if (isExisting) classes.push('existing-holiday');
        if (isHoliday) classes.push('public-holiday');

        return classes.join(' ');
    };

    const tileContent = ({ date, view }) => {
        if (view !== 'month') return null;

        const dateStr = formatDateForAPI(date);
        const holidays = holidayLookup.get(dateStr);

        if (holidays && holidays.length > 0) {
            // Create tooltip with all holiday names
            const tooltipText = holidays.map(h => h.name).join('\n');

            return (
                <div className="holiday-event-wrapper">
                    {holidays.map((holiday, index) => (
                        <div key={index} className="holiday-event">
                            <span className="holiday-event-text" title={tooltipText}>
                                {holiday.name}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }

        return null;
    };

    const handleDone = () => {
        if (selectedDates.length === 0) {
            setError('Please select at least one date.');
            return;
        }
        onDone(selectedDates);
        setSelectedDates([]);
        setError(null);
    };

    const handleCancel = () => {
        setSelectedDates([]);
        setError(null);
        onClose();
    };

    return (
        <Modal
            show={show}
            maxWidth="min(750px, 95vw)"
            title="Select Holiday Dates"
            onclose={handleCancel}
            body={
                <div className="holiday-calendar-wrapper" style={{ padding: '10px' }}>
                    {loading && (
                        <Message info>
                            <Message.Header>Loading Public Holidays</Message.Header>
                            <p>Fetching USA and Canada public holidays...</p>
                        </Message>
                    )}

                    {error && (
                        <Message warning>
                            <p>{error}</p>
                        </Message>
                    )}

                    <Calendar
                        minDate={new Date()}
                        onClickDay={handleDateClick}
                        tileClassName={tileClassName}
                        tileContent={tileContent}
                        showDoubleView={false}
                        selectRange={false}
                        value={null}
                    />
                </div>
            }
            footer={
                <div className="modal-footer d-flex justify-content-end gap-2">
                    <button
                        type="button"
                        className="yr-cancelbtn linear-gradient-cancel btn-sm yr-btn-small"
                        onClick={handleCancel}
                    >
                       <span>Cancel</span>
                    </button>
                    <button
                        type="button"
                        className="btn linear-gradient yr-submitbtn btn-sm"
                        onClick={handleDone}
                        disabled={selectedDates.length === 0}
                    >
                        Done
                    </button>
                </div>
            }
        />
    );
};

export default HolidayCalendarModal;
